import React, { useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import LanguageToggle from '../components/LanguageToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StudyPlannerPage = () => {
  const { t, language } = useLanguage();
  const [gradeLevel, setGradeLevel] = useState('11');
  const [targetExams, setTargetExams] = useState('');
  const [weeklyHours, setWeeklyHours] = useState(10);
  const [weakTopics, setWeakTopics] = useState('');
  const [studyPlan, setStudyPlan] = useState('');
  const [parsedPlan, setParsedPlan] = useState(null);
  const [expandedTopics, setExpandedTopics] = useState({});
  const [topicContents, setTopicContents] = useState({});
  const [loadingContents, setLoadingContents] = useState({});
  const [loading, setLoading] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);

  const handleCreatePlan = async (e) => {
    e.preventDefault();
    if (!targetExams.trim()) return;

    setLoading(true);
    setStudyPlan('');

    try {
      const studentProfile = {
        grade_level: parseInt(gradeLevel),
        target_exams: targetExams
      };

      const performanceData = weakTopics.trim() ? {
        weak_topics: weakTopics.split(',').map(t => t.trim()).filter(Boolean)
      } : undefined;

      const response = await axios.post(`${API}/study-plan`, {
        student_profile: studentProfile,
        performance_data: performanceData,
        weekly_time: weeklyHours
      });

      console.log('=== STUDY PLAN RECEIVED ===');
      console.log('Response data:', response.data);
      
      // Use structured_plan if available (NEW FORMAT)
      if (response.data.structured_plan && response.data.structured_plan.days) {
        console.log('✅ Using structured plan format');
        const structuredPlan = response.data.structured_plan;
        
        // Convert structured format to display format
        const sections = structuredPlan.days.map(day => ({
          day: `${language === 'tr' ? 'Gün' : 'Day'} ${day.dayIndex}`,
          topics: day.sessions.map(session => 
            `${session.topic} (${session.type}, ${session.estimatedMinutes} min)`
          )
        }));
        
        setParsedPlan(sections);
        setStudyPlan(response.data.study_plan || JSON.stringify(structuredPlan, null, 2));
        console.log('Parsed sections from structured plan:', sections);
      } else {
        // Fallback to text parsing (OLD FORMAT)
        console.log('⚠️ No structured plan, using text parsing');
        const planText = response.data.study_plan;
        setStudyPlan(planText);
        
        console.log('Plan text:', planText);
        console.log('Plan length:', planText.length);
        
        // Parse the plan to extract topics/sections
        const parsed = parsePlanToSections(planText);
        console.log('Parsed sections:', parsed);
        console.log('Number of sections:', parsed?.length || 0);
        
        // If parsing failed, create a simple fallback structure
        if (!parsed || parsed.length === 0) {
          console.warn('⚠️ No sections parsed! Creating fallback structure...');
          
          const fallbackPlan = [{
            day: 'Week Overview',
            topics: ['View full plan below']
          }];
          
          setParsedPlan(fallbackPlan);
          console.log('Using fallback plan structure');
        } else {
          setParsedPlan(parsed);
        }
      }
    } catch (error) {
      console.error('Error creating study plan:', error);
      setStudyPlan(t('error') + ': ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const parsePlanToSections = (planText) => {
    console.log('🔍 PARSING PLAN...');
    
    // Parse the AI-generated plan into structured sections
    const lines = planText.split('\n');
    console.log(`Total lines: ${lines.length}`);
    
    const sections = [];
    let currentDay = null;
    let currentTopics = [];
    let dayCount = 0;
    let topicCount = 0;
    
    lines.forEach((line, index) => {
      const trimmed = line.trim();
      
      if (!trimmed) return; // Skip empty lines
      
      // Detect day headers (more flexible patterns)
      // Match: "Day 1:", "Monday:", "Pazartesi:", "1. Gün", etc.
      const dayPattern = /^(Day\s+\d+|Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday|Pazartesi|Salı|Çarşamba|Perşembe|Cuma|Cumartesi|Pazar|\d+\.\s*Gün)/i;
      
      if (dayPattern.test(trimmed)) {
        dayCount++;
        console.log(`✅ Day ${dayCount} found at line ${index}: "${trimmed}"`);
        
        if (currentDay && currentTopics.length > 0) {
          sections.push({ day: currentDay, topics: currentTopics });
          console.log(`   Added section with ${currentTopics.length} topics`);
        }
        currentDay = trimmed.replace(':', '').replace(/^\d+\.\s*/, '');
        currentTopics = [];
      }
      // Detect topic lines with more flexible patterns
      // Match: "- Topic", "• Topic", "1. Topic", "* Topic"
      else if (/^[-•*\d+.)]\s*(.+)/.test(trimmed) && trimmed.length > 5) {
        const topicMatch = trimmed.match(/^[-•*\d+.)]\s*(.+)/);
        if (topicMatch && topicMatch[1]) {
          const topicName = topicMatch[1].trim();
          // Extract topic name (before any time/duration info)
          const cleanTopic = topicName
            .replace(/\s*\(\d+\s*(min|minutes|dk|dakika)\)/gi, '')
            .replace(/\s*-\s*\d+\s*(min|minutes|dk|dakika)$/gi, '')
            .replace(/\s*\d+\s*(min|minutes|dk|dakika)$/gi, '')
            .trim();
          
          // Only add if it's meaningful content (not just metadata)
          if (cleanTopic.length > 3 && !cleanTopic.match(/^\d+\s*(min|hour|saat)/i)) {
            topicCount++;
            currentTopics.push(cleanTopic);
            console.log(`   📝 Topic ${topicCount}: "${cleanTopic}"`);
          }
        }
      }
    });
    
    // Push last day
    if (currentDay && currentTopics.length > 0) {
      sections.push({ day: currentDay, topics: currentTopics });
      console.log(`✅ Added final section with ${currentTopics.length} topics`);
    }
    
    // Debug summary
    console.log('📊 PARSING SUMMARY:');
    console.log(`   Days found: ${dayCount}`);
    console.log(`   Topics found: ${topicCount}`);
    console.log(`   Sections created: ${sections.length}`);
    console.log('   Full sections:', sections);
    
    return sections;
  };

  const fetchTopicContent = async (topic) => {
    setLoadingContents(prev => ({ ...prev, [topic]: true }));
    
    try {
      const response = await axios.post(`${API}/generate-topic-content`, {
        topic: topic,
        exams: targetExams,
        grade_level: gradeLevel
      }, { withCredentials: true });
      
      setTopicContents(prev => ({ ...prev, [topic]: response.data.content }));
    } catch (error) {
      console.error('Error fetching topic content:', error);
      setTopicContents(prev => ({ 
        ...prev, 
        [topic]: 'İçerik yüklenirken hata oluştu / Error loading content' 
      }));
    } finally {
      setLoadingContents(prev => ({ ...prev, [topic]: false }));
    }
  };

  const toggleTopic = (topic) => {
    const isExpanded = expandedTopics[topic];
    setExpandedTopics(prev => ({ ...prev, [topic]: !isExpanded }));
    
    // Fetch content if not already loaded and now expanding
    if (!isExpanded && !topicContents[topic] && !loadingContents[topic]) {
      fetchTopicContent(topic);
    }
  };

  const generatePDF = async () => {
    setGeneratingPDF(true);
    
    try {
      // Prepare sections with loaded contents
      // If no parsed plan, create a simple structure
      let sectionsWithContent = [];
      
      if (parsedPlan && parsedPlan.length > 0) {
        sectionsWithContent = parsedPlan.map(section => ({
          day: section.day,
          topics: section.topics,
          contents: section.topics.reduce((acc, topic) => {
            acc[topic] = topicContents[topic] || 'İçerik henüz yüklenmedi / Content not yet loaded';
            return acc;
          }, {})
        }));
      } else {
        // Fallback: Create simple structure from plan text
        sectionsWithContent = [{
          day: 'Week Overview',
          topics: ['Full Study Plan'],
          contents: {
            'Full Study Plan': studyPlan || 'Plan content'
          }
        }];
      }
      
      const response = await axios.post(
        `${API}/study-plan/generate-pdf`,
        {
          plan_text: studyPlan,
          sections: sectionsWithContent,
          student_name: 'BridgePrep Student',
          exams: targetExams,
          grade: gradeLevel,
          week_number: 1
        },
        {
          withCredentials: true,
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `BridgePrep_Week1_StudyPlan.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('PDF oluşturulurken hata oluştu / Error generating PDF');
    } finally {
      setGeneratingPDF(false);
    }
  };

  const handleReset = () => {
    setGradeLevel('11');
    setTargetExams('');
    setWeeklyHours(10);
    setWeakTopics('');
    setStudyPlan('');
    setParsedPlan(null);
    setExpandedTopics({});
    setTopicContents({});
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <LanguageToggle />
      <Navbar title={t('study_planner')} />

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          
          {/* Input Panel */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
              <span className="text-3xl mr-3">📊</span>
              {t('create_plan')}
            </h2>
            
            <form onSubmit={handleCreatePlan} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('grade_level')}
                </label>
                <select
                  value={gradeLevel}
                  onChange={(e) => setGradeLevel(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="grade-select"
                >
                  <option value="9">9. Sınıf / Grade 9</option>
                  <option value="10">10. Sınıf / Grade 10</option>
                  <option value="11">11. Sınıf / Grade 11</option>
                  <option value="12">12. Sınıf / Grade 12</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('target_exams')}
                </label>
                <input
                  type="text"
                  value={targetExams}
                  onChange={(e) => setTargetExams(e.target.value)}
                  placeholder="AP Physics 1, AP Calculus AB, TYT, AYT"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  data-testid="exams-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('weekly_hours')}
                </label>
                <div className="flex items-center space-x-4">
                  <input
                    type="range"
                    value={weeklyHours}
                    onChange={(e) => setWeeklyHours(parseInt(e.target.value))}
                    min="5"
                    max="40"
                    step="1"
                    className="flex-1"
                    data-testid="hours-slider"
                  />
                  <span className="text-lg font-semibold text-gray-800 w-16 text-right">
                    {weeklyHours} {t('hours')}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('weak_topics')}
                </label>
                <textarea
                  value={weakTopics}
                  onChange={(e) => setWeakTopics(e.target.value)}
                  placeholder="Kinematics, Derivatives, Chemical Bonding (comma separated)"
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  data-testid="topics-input"
                />
              </div>

              <div className="flex space-x-4">
                <button
                  type="submit"
                  disabled={loading || !targetExams.trim()}
                  className="flex-1 bg-gradient-to-r from-yellow-600 to-orange-600 text-white px-6 py-3 rounded-xl hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 font-semibold"
                  data-testid="create-plan-button"
                >
                  {loading ? t('loading') : t('create_plan')}
                </button>
                <button
                  type="button"
                  onClick={handleReset}
                  className="bg-gray-200 text-gray-800 px-6 py-3 rounded-xl hover:bg-gray-300 transition-colors font-semibold"
                  data-testid="reset-button"
                >
                  {t('new_chat')}
                </button>
              </div>
            </form>
          </div>

          {/* Results Panel */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-800 flex items-center">
                <span className="text-3xl mr-3">📅</span>
                {t('study_plan_result')}
              </h2>
              
              {studyPlan && (
                <button
                  onClick={generatePDF}
                  disabled={generatingPDF}
                  className="bg-gradient-to-r from-red-500 to-orange-500 text-white px-6 py-3 rounded-xl hover:from-red-600 hover:to-orange-600 transition-all font-semibold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {generatingPDF ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span>PDF Oluşturuluyor...</span>
                    </>
                  ) : (
                    <>
                      <span>📥</span>
                      <span>PDF İndir / Download PDF</span>
                    </>
                  )}
                </button>
              )}
            </div>
            
            {studyPlan && (
              <>
                {/* Show parsed plan if available */}
                {parsedPlan && parsedPlan.length > 0 && (
                  <div className="space-y-6 mb-8" data-testid="study-plan-result">
                    <h3 className="text-lg font-bold text-gray-700 mb-4">📋 Günlük Plan / Daily Schedule</h3>
                    {parsedPlan.map((section, sectionIdx) => (
                  <div key={sectionIdx} className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-6">
                    <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                      <span className="text-2xl mr-3">📅</span>
                      {section.day}
                    </h3>
                    
                    <div className="space-y-3">
                      {section.topics.map((topic, topicIdx) => (
                        <div key={topicIdx} className="bg-white rounded-lg shadow-sm">
                          <button
                            onClick={() => toggleTopic(topic)}
                            className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors rounded-lg"
                          >
                            <div className="flex items-center space-x-3">
                              <span className="text-lg">
                                {expandedTopics[topic] ? '📖' : '📝'}
                              </span>
                              <span className="font-medium text-gray-800 text-left">
                                {topic}
                              </span>
                            </div>
                            <svg 
                              className={`w-5 h-5 text-gray-500 transition-transform ${expandedTopics[topic] ? 'rotate-180' : ''}`}
                              fill="none" 
                              stroke="currentColor" 
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                          
                          {expandedTopics[topic] && (
                            <div className="px-4 pb-4 border-t border-gray-100">
                              {loadingContents[topic] ? (
                                <div className="flex items-center justify-center py-8">
                                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
                                  <span className="ml-3 text-gray-600">İçerik yükleniyor...</span>
                                </div>
                              ) : topicContents[topic] ? (
                                <div className="prose max-w-none mt-4">
                                  <div className="text-gray-700 whitespace-pre-wrap">
                                    {topicContents[topic]}
                                  </div>
                                </div>
                              ) : (
                                <div className="text-center py-4 text-gray-500">
                                  <p>İçerik yüklenemedi</p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                </div>
              )}
              
              {/* Show full plan text only if parsing failed or no parsed plan available */}
              {(!parsedPlan || parsedPlan.length === 0) && (
                <div className="mt-8">
                  <h3 className="text-lg font-bold text-gray-700 mb-4">📄 Tam Plan / Full Plan</h3>
                  <div className="prose max-w-none" data-testid="study-plan-result">
                    <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-6 rounded-xl">
                      <div className="whitespace-pre-wrap text-gray-800">{studyPlan}</div>
                    </div>
                  </div>
                </div>
              )}
              </>
            )}
            
            {!studyPlan && (
              <div className="text-center text-gray-400 mt-12">
                <svg className="w-24 h-24 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <p className="text-lg">{t('create_plan')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudyPlannerPage;